// MoreDropDown.jsx (MENTEE SIDE)
import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Classes from './RecordTable.module.css';
import { useSnackbar } from 'notistack';
import { CircularProgress } from '@mui/material';
import backend from '../../../../../../../api';
import RescheduleModal from './RescheduleModal';
import DeleteConfirmPopup from '../../../../../../GlobalComponents/DeleteConfirmPopup/DeleteConfirmPopup';

export default function MoreDropDown({ booking }) {
  const [dropDown, setDropDown] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigateTo = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  // mentor availability is coming on booking per your note
  const mentorAvailability = booking?.mentorAvailability;
  const bookingId = booking?._id;

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropDown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ─────────────────────────────
   * Confirm Popup State
   * mode: 'delete' | 'cancel' | null
   * ───────────────────────────── */
  const [confirm, setConfirm] = useState({ open: false, mode: null });
  const openDeleteConfirm = () => setConfirm({ open: true, mode: 'delete' });
  const openCancelConfirm = () => setConfirm({ open: true, mode: 'cancel' });
  const closeConfirm = () => setConfirm({ open: false, mode: null });

  /* ─────────────────────────────
   * Actions (run AFTER confirm)
   * ───────────────────────────── */

  // DELETE booking
  const doDelete = async () => {
    try {
      setLoading(true);
      await backend.delete(`/bookings/${bookingId}`);
      enqueueSnackbar('Booking deleted.', { variant: 'success' });
      setDropDown(false);
      window.location.reload();
    } catch (error) {
      console.error('Error deleting booking:', error);
      enqueueSnackbar(error?.response?.data?.error || 'Failed to delete booking', { variant: 'error' });
    } finally {
      setLoading(false);
      closeConfirm();
    }
  };

  // CANCEL/REFUND booking
  const doRefund = async () => {
    try {
      const stripeAccountId = booking?.stripeAccountId;
      const paymentIntentId = booking?.paymentIntentId;
      const amount = booking?.bookingFee || 0;
      const platformFee = booking?.platformFee || 0;

      if (booking.status?.label == 'Canceled') {
        enqueueSnackbar('This booking is already Canceled.', { variant: 'error' });
        closeConfirm();
        return;
      }

      if (amount <= 0) {
        enqueueSnackbar('Invalid refund amount.', { variant: 'error' });
        closeConfirm();
        return;
      }
      if (booking?.status === 'Refunded') {
        enqueueSnackbar('This booking is already refunded.', { variant: 'error' });
        closeConfirm();
        return;
      }

      setLoading(true);
      const resp = await backend.post('/pay/refund', {
        stripeAccountId,
        paymentIntentId,
        amount,
        platformFee,
        bookingId,
      });

      if (resp.status === 200) {
        enqueueSnackbar('Cancel request initiated successfully.', { variant: 'success' });
        setDropDown(false);
        window.location.reload();
      } else {
        enqueueSnackbar('Refund failed: ' + (resp.data?.error || 'Unknown error'), { variant: 'error' });
      }
    } catch (err) {
      console.error('Error processing refund:', err);
      enqueueSnackbar('Error processing refund: ' + (err.response?.data?.error || err.message), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
      closeConfirm();
    }
  };

  const handleReschedule = () => {
    if (booking?.status?.label === 'Canceled') {
      enqueueSnackbar('Not allowed for canceled bookings', { variant: 'error' });
    } else {
      setRescheduleOpen(true);
    }
  };

  return loading ? (
    <CircularProgress />
  ) : (
    <div className={Classes.dropDown} ref={dropdownRef}>
      <div className="flexAlign10 pointer mr10" onClick={() => setDropDown(!dropDown)}>
        <div className={Classes.dropdownIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" height="15px" viewBox="0 0 128 512">
            <path d="M64 360a56 56 0 1 0 0 112 56 56 0 1 0 0-112zm0-160a56 56 0 1 0 0 112 56 56 0 1 0 0-112zM120 96A56 56 0 1 0 8 96a56 56 0 1 0 112 0z" />
          </svg>
        </div>
      </div>

      <AnimatePresence>
        {dropDown && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.15 }}
            className={Classes.dropDownBody}
            style={{ top: '13px', right: '30px' }}
          >
            <div className={Classes.dropDownBodyInner}>
              <div
                className={Classes.dropDownElem}
                style={{ padding: '5px 10px' }}
                onClick={() => {
                  setDropDown(false);
                  handleReschedule();
                }}
              >
                <h5> Reschedule </h5>
              </div>

              <div className={Classes.dropDownLine}></div>

              <div
                className={Classes.dropDownElem}
                style={{ padding: '5px 10px' }}
                onClick={() => {
                  // open confirm for CANCEL instead of window.confirm
                  booking.status?.label == 'Canceled' ? enqueueSnackbar('This booking is already Canceled.', { variant: 'error' }) : openCancelConfirm();
                }}
              >
                <h5> Cancel </h5>
              </div>

              <div className={Classes.dropDownLine}></div>

              <div
                className={Classes.dropDownElem}
                style={{ padding: '5px 10px', color: 'red' }}
                onClick={() => {
                  // open confirm for DELETE instead of window.confirm
                  openDeleteConfirm();
                }}
              >
                <h5>Delete</h5>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm Popup (Cancel | Delete) */}
      <AnimatePresence>
        {confirm.open && (
          <DeleteConfirmPopup
            handleClose={closeConfirm}
            handleSubmit={confirm.mode === 'delete' ? doDelete : doRefund}
            title={
              confirm.mode === 'delete'
                ? 'Are you sure you want to delete?'
                : 'Are you sure you want to cancel this booking?'
            }
            description={
              confirm.mode === 'delete'
                ? "This action can’t be undone."
                : 'This will attempt a refund (if eligible).'
            }
            confirmLabel={confirm.mode === 'delete' ? 'Delete' : 'Confirm'}
            confirmTone={confirm.mode === 'delete' ? 'danger' : 'danger'}
            loading={loading}
          />
        )}
      </AnimatePresence>

      {/* Reschedule modal */}
      {rescheduleOpen && mentorAvailability && (
        <RescheduleModal
          open={rescheduleOpen}
          onClose={() => setRescheduleOpen(false)}
          booking={booking}
          availability={mentorAvailability}
          onSuccess={() => {
            window.location.reload();
          }}
          onError={(msg) => enqueueSnackbar(msg || 'Failed to reschedule', { variant: 'error' })}
        />
      )}

      <input type="hidden" aria-label="Without label" readOnly />
    </div>
  );
}
